import { useState, useEffect, useRef } from 'react';
import { db, storage } from '../../services/firebase';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp, Timestamp, doc, setDoc, getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Plus, ChevronLeft, ChevronRight, Eye } from 'lucide-react';

interface Story {
  id: string;
  uid: string;
  displayName: string;
  photoURL?: string;
  mediaURL: string;
  mediaType: 'image' | 'video';
  text?: string;
  createdAt: any;
  expiresAt: any;
  viewCount?: number;
  viewedByMe?: boolean;
}

interface StoryGroup {
  uid: string;
  displayName: string;
  photoURL?: string;
  stories: Story[];
  hasUnread: boolean;
}

interface Props {
  currentUser: any;
  friendUids: string[];
}

const STORY_DURATION = 5000; // ms per story slide

export const StoriesBar = ({ currentUser, friendUids }: Props) => {
  const [storyGroups, setStoryGroups] = useState<StoryGroup[]>([]);
  const [myStories, setMyStories] = useState<Story[]>([]);
  const [viewer, setViewer] = useState<{ groups: StoryGroup[]; groupIdx: number; storyIdx: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load stories (own + friends) ──
  useEffect(() => {
    const now = Timestamp.now();
    const allUids = [currentUser.uid, ...friendUids];

    // Firestore "in" limit is 30
    const chunks: string[][] = [];
    for (let i = 0; i < allUids.length; i += 30) chunks.push(allUids.slice(i, i + 30));

    const unsubs: (() => void)[] = [];
    const allStoriesMap = new Map<string, Story>();

    chunks.forEach(chunk => {
      const q = query(
        collection(db, 'stories'),
        where('uid', 'in', chunk),
        where('expiresAt', '>', now),
        orderBy('expiresAt', 'asc'),
        orderBy('createdAt', 'asc'),
      );
      const unsub = onSnapshot(q, async snap => {
        for (const d of snap.docs) {
          const data = d.data();
          // Check if current user has viewed this story
          let viewedByMe = false;
          try {
            const viewSnap = await getDoc(doc(db, 'stories', d.id, 'views', currentUser.uid));
            viewedByMe = viewSnap.exists();
          } catch { /* ignore */ }

          allStoriesMap.set(d.id, {
            id: d.id,
            uid: data.uid,
            displayName: data.displayName || 'Usuario',
            photoURL: data.photoURL,
            mediaURL: data.mediaURL,
            mediaType: data.mediaType || 'image',
            text: data.text,
            createdAt: data.createdAt,
            expiresAt: data.expiresAt,
            viewCount: data.viewCount || 0,
            viewedByMe,
          });
        }
        buildGroups();
      });
      unsubs.push(unsub);
    });

    const buildGroups = () => {
      const byUid = new Map<string, Story[]>();
      allStoriesMap.forEach(story => {
        if (!byUid.has(story.uid)) byUid.set(story.uid, []);
        byUid.get(story.uid)!.push(story);
      });

      const myList = byUid.get(currentUser.uid) || [];
      setMyStories(myList);

      const groups: StoryGroup[] = [];
      byUid.forEach((stories, uid) => {
        if (uid === currentUser.uid) return;
        const first = stories[0];
        groups.push({
          uid,
          displayName: first.displayName,
          photoURL: first.photoURL,
          stories: stories.sort((a, b) => a.createdAt?.toMillis?.() - b.createdAt?.toMillis?.()),
          hasUnread: stories.some(s => !s.viewedByMe),
        });
      });
      setStoryGroups(groups.sort((a, b) => (b.hasUnread ? 1 : 0) - (a.hasUnread ? 1 : 0)));
    };

    return () => unsubs.forEach(u => u());
  }, [currentUser.uid, friendUids.join(',')]);

  // ── Upload story ──
  const handleUpload = async (file: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const isVideo = file.type.startsWith('video/');
      const path = `stories/${currentUser.uid}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      const mediaURL = await getDownloadURL(storageRef);

      const now = new Date();
      const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);

      await addDoc(collection(db, 'stories'), {
        uid: currentUser.uid,
        displayName: currentUser.displayName || 'Tú',
        photoURL: currentUser.photoURL || null,
        mediaURL,
        mediaType: isVideo ? 'video' : 'image',
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromDate(expiresAt),
        viewCount: 0,
      });
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  // ── Viewer logic ──
  const openViewer = (groups: StoryGroup[], groupIdx: number, storyIdx = 0) => {
    setViewer({ groups, groupIdx, storyIdx });
  };

  const closeViewer = () => {
    setViewer(null);
    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);
  };

  const goNext = () => {
    if (!viewer) return;
    const { groups, groupIdx, storyIdx } = viewer;
    const group = groups[groupIdx];
    if (storyIdx < group.stories.length - 1) {
      setViewer({ ...viewer, storyIdx: storyIdx + 1 });
    } else if (groupIdx < groups.length - 1) {
      setViewer({ ...viewer, groupIdx: groupIdx + 1, storyIdx: 0 });
    } else {
      closeViewer();
    }
    setProgress(0);
  };

  const goPrev = () => {
    if (!viewer) return;
    const { groups, groupIdx, storyIdx } = viewer;
    if (storyIdx > 0) {
      setViewer({ ...viewer, storyIdx: storyIdx - 1 });
    } else if (groupIdx > 0) {
      const prevGroup = groups[groupIdx - 1];
      setViewer({ ...viewer, groupIdx: groupIdx - 1, storyIdx: prevGroup.stories.length - 1 });
    }
    setProgress(0);
  };

  // Mark story as viewed
  useEffect(() => {
    if (!viewer) return;
    const { groups, groupIdx, storyIdx } = viewer;
    const story = groups[groupIdx]?.stories[storyIdx];
    if (!story || story.uid === currentUser.uid) return;

    setDoc(doc(db, 'stories', story.id, 'views', currentUser.uid), {
      viewedAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});
  }, [viewer?.groupIdx, viewer?.storyIdx]);

  // Auto-advance progress bar
  useEffect(() => {
    if (!viewer) return;
    const { groups, groupIdx, storyIdx } = viewer;
    const story = groups[groupIdx]?.stories[storyIdx];
    if (!story) return;

    if (story.mediaType === 'video') return; // video controls itself

    if (progressRef.current) clearInterval(progressRef.current);
    setProgress(0);

    const step = 100 / (STORY_DURATION / 100);
    progressRef.current = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(progressRef.current);
          goNext();
          return 0;
        }
        return p + step;
      });
    }, 100);

    return () => { if (progressRef.current) clearInterval(progressRef.current); };
  }, [viewer?.groupIdx, viewer?.storyIdx]);

  const getInitials = (name: string) => name?.slice(0, 2).toUpperCase() || 'U';

  const currentStory = viewer
    ? viewer.groups[viewer.groupIdx]?.stories[viewer.storyIdx]
    : null;

  const myGroup: StoryGroup = {
    uid: currentUser.uid,
    displayName: 'Mi historia',
    photoURL: currentUser.photoURL,
    stories: myStories,
    hasUnread: false,
  };

  const allGroupsForViewer = [myGroup, ...storyGroups].filter(g => g.stories.length > 0);

  return (
    <>
      {/* ── Stories row ── */}
      <div className="stories-bar">

        {/* Add story bubble */}
        <div
          className="story-bubble story-bubble--add"
          onClick={() => {
            if (myStories.length > 0) {
              openViewer(allGroupsForViewer, 0);
            } else {
              fileInputRef.current?.click();
            }
          }}
        >
          <div className="story-bubble__ring story-bubble__ring--mine">
            <div className="story-bubble__avatar">
              {currentUser.photoURL
                ? <img src={currentUser.photoURL} alt="" />
                : <span>{getInitials(currentUser.displayName || 'Tú')}</span>}
              <div
                className="story-bubble__add-icon"
                onClick={e => {
                  // Siempre permite agregar nueva story aunque ya tenga
                  if (myStories.length > 0) {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }
                }}
              >
                {uploading ? <div className="story-upload-spinner" /> : <Plus size={12} />}
              </div>
            </div>
          </div>
          <span className="story-bubble__name">{myStories.length > 0 ? 'Tu historia' : 'Agregar'}</span>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); e.target.value = ''; }}
        />

        {/* Friends' stories */}
        {storyGroups.map((group) => (
          <div
            key={group.uid}
            className="story-bubble"
            onClick={() => openViewer(allGroupsForViewer, allGroupsForViewer.findIndex(g => g.uid === group.uid))}
          >
            <div className={`story-bubble__ring ${group.hasUnread ? 'story-bubble__ring--unread' : 'story-bubble__ring--seen'}`}>
              <div className="story-bubble__avatar">
                {group.photoURL
                  ? <img src={group.photoURL} alt="" />
                  : <span>{getInitials(group.displayName)}</span>}
              </div>
            </div>
            <span className="story-bubble__name">{group.displayName.split(' ')[0]}</span>
          </div>
        ))}
      </div>

      {/* ── Story Viewer Fullscreen ── */}
      {viewer && currentStory && (
        <div className="story-viewer" onClick={closeViewer}>
          <div className="story-viewer__inner" onClick={e => e.stopPropagation()}>

            {/* Progress bars */}
            <div className="story-viewer__bars">
              {viewer.groups[viewer.groupIdx].stories.map((_, i) => (
                <div key={i} className="story-viewer__bar-track">
                  <div
                    className="story-viewer__bar-fill"
                    style={{
                      width: i < viewer.storyIdx ? '100%'
                        : i === viewer.storyIdx ? `${progress}%`
                        : '0%'
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Header */}
            <div className="story-viewer__header">
              <div className="story-viewer__user">
                <div className="story-viewer__av">
                  {viewer.groups[viewer.groupIdx].photoURL
                    ? <img src={viewer.groups[viewer.groupIdx].photoURL} alt="" />
                    : <span>{getInitials(viewer.groups[viewer.groupIdx].displayName)}</span>}
                </div>
                <div>
                  <div className="story-viewer__name">{viewer.groups[viewer.groupIdx].displayName}</div>
                  <div className="story-viewer__time">
                    {currentStory.createdAt?.toDate
                      ? currentStory.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : ''}
                  </div>
                </div>
              </div>
              <button className="story-viewer__close" onClick={closeViewer}><X size={20} /></button>
            </div>

            {/* Media */}
            <div className="story-viewer__media">
              {currentStory.mediaType === 'video' ? (
                <video
                  ref={videoRef}
                  src={currentStory.mediaURL}
                  autoPlay
                  playsInline
                  onEnded={goNext}
                  className="story-viewer__video"
                />
              ) : (
                <img src={currentStory.mediaURL} alt="" className="story-viewer__img" />
              )}
              {currentStory.text && (
                <div className="story-viewer__text-overlay">{currentStory.text}</div>
              )}
            </div>

            {/* Tap zones */}
            <div className="story-viewer__tap-prev" onClick={goPrev} />
            <div className="story-viewer__tap-next" onClick={goNext} />

            {/* Nav arrows */}
            {(viewer.storyIdx > 0 || viewer.groupIdx > 0) && (
              <button className="story-viewer__nav story-viewer__nav--prev" onClick={goPrev}>
                <ChevronLeft size={22} />
              </button>
            )}
            <button className="story-viewer__nav story-viewer__nav--next" onClick={goNext}>
              <ChevronRight size={22} />
            </button>

            {/* View count (only own stories) */}
            {currentStory.uid === currentUser.uid && (
              <div className="story-viewer__views">
                <Eye size={14} /> {currentStory.viewCount || 0}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};